import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import { BookOpen, Award, MessageSquare, Clock, AlertCircle, CheckCircle, FileText } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../../api';

// ... (interfaces remain the same)

const StudentDashboard: React.FC = () => {
  const [enrolledCourses, setEnrolledCourses] = useState<EnrolledCourse[]>([]);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [stats, setStats] = useState<DashboardStats>({
    courseCount: 0,
    quizCount: 0,
    unreadMessages: 0
  });
  const [loading, setLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0);

  const fetchDataWithRetry = async (fetchFunction: () => Promise<any>, maxRetries = 3) => {
    let attempts = 0;
    while (attempts < maxRetries) {
      try {
        return await fetchFunction();
      } catch (error) {
        attempts++;
        if (attempts >= maxRetries) {
          throw error;
        }
        // Exponential backoff
        await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, attempts)));
      }
    }
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Configure timeout for dashboard requests (30 seconds instead of 60)
      const dashboardConfig = { timeout: 30000 };
      
      const fetchEnrolledCourses = () => api.get('/student/dashboard/enrolled-courses', dashboardConfig)
        .then(res => {
          setEnrolledCourses(res.data.courses);
          return res.data.courses.length;
        });

      const fetchQuickStats = () => api.get('/student/dashboard/quick-stats', dashboardConfig)
        .then(res => {
          setStats(res.data.data);
          return res.data.data;
        });

      const fetchRecentActivity = () => api.get('/student/dashboard/recent-activity', dashboardConfig)
        .then(res => {
          setActivities(res.data.activities);
          return res.data.activities.length;
        });

      // Execute in parallel with retry logic
      const [coursesCount, statsData, activitiesCount] = await Promise.all([
        fetchDataWithRetry(fetchEnrolledCourses),
        fetchDataWithRetry(fetchQuickStats),
        fetchDataWithRetry(fetchRecentActivity)
      ]);

      // Update stats with actual counts if needed
      setStats(prev => ({
        ...prev,
        courseCount: coursesCount,
        quizCount: statsData.quizCount,
        unreadMessages: statsData.unreadMessages
      }));

    } catch (error) {
      console.error('Dashboard fetch error:', error);
      
      if (error.code === 'ECONNABORTED') {
        toast.error('Le serveur met trop de temps à répondre. Veuillez réessayer.');
      } else {
        toast.error('Erreur lors du chargement du tableau de bord');
      }
      
      // Retry logic
      if (retryCount < 3) {
        setRetryCount(prev => prev + 1);
        setTimeout(fetchDashboardData, 2000 * retryCount);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const abortController = new AbortController();
    
    fetchDashboardData();

    return () => {
      abortController.abort();
    };
  }, []);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-brand-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <h1 className="text-3xl font-bold text-gray-900">Tableau de bord Étudiant</h1>

        {/* Quick Links with counts */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link 
            to="/student/courses" 
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 flex flex-col items-center justify-center hover:shadow-md transition-shadow"
          >
            <BookOpen className="h-12 w-12 text-brand-600 mb-3" />
            <span className="text-lg font-semibold text-gray-800">
              Mes Cours {stats.courseCount > 0 && `(${stats.courseCount})`}
            </span>
          </Link>
          
          <Link 
            to="/student/quizzes" 
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 flex flex-col items-center justify-center hover:shadow-md transition-shadow"
          >
            <Award className="h-12 w-12 text-brand-600 mb-3" />
            <span className="text-lg font-semibold text-gray-800">
              Mes Quiz {stats.quizCount > 0 && `(${stats.quizCount})`}
            </span>
          </Link>
          
          <Link 
            to="/student/messages" 
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 flex flex-col items-center justify-center hover:shadow-md transition-shadow"
          >
            <MessageSquare className="h-12 w-12 text-brand-600 mb-3" />
            <span className="text-lg font-semibold text-gray-800">
              Messagerie {stats.unreadMessages > 0 && `(${stats.unreadMessages})`}
            </span>
          </Link>
        </div>

        {/* Enrolled Courses */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Mes Cours Inscrits</h2>
          
          {enrolledCourses.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {enrolledCourses.map(course => (
                <Link 
                  to={`/student/courses/${course._id}`} 
                  key={course._id} 
                  className="block border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow"
                >
                  {course.thumbnail ? (
                    <img 
                      src={course.thumbnail} 
                      alt={course.title} 
                      className="w-full h-32 object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = '/placeholder-course.jpg';
                      }}
                    />
                  ) : (
                    <div className="w-full h-32 bg-gray-100 flex items-center justify-center">
                      <BookOpen className="h-12 w-12 text-gray-400" />
                    </div>
                  )}
                  
                  <div className="p-4">
                    <h3 className="font-semibold text-gray-900 mb-1 truncate">{course.title}</h3>
                    <p className="text-sm text-gray-600 mb-2">{course.professor.name}</p>
                    
                    <div className="flex items-center text-sm text-gray-700">
                      <Clock className="h-4 w-4 mr-1 text-gray-500" />
                      Progression: {Math.round(course.userProgress)}%
                    </div>
                    
                    <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                      <div
                        className="bg-brand-600 h-2 rounded-full"
                        style={{ width: `${course.userProgress}%` }}
                      ></div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <AlertCircle className="h-12 w-12 mx-auto mb-3" />
              <p>Vous n'êtes inscrit à aucun cours pour le moment.</p>
              <Link 
                to="/student/courses" 
                className="mt-3 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500"
              >
                Découvrir les cours
              </Link>
            </div>
          )}
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Activité Récente</h2>
          
          {activities.length > 0 ? (
            <div className="space-y-4">
              {activities.map(activity => (
                <div 
                  key={activity._id} 
                  className={`flex items-start p-3 rounded-lg transition-colors ${!activity.read ? 'bg-brand-50' : 'hover:bg-gray-50'}`}
                >
                  <div className="flex-shrink-0 mt-1">
                    {activity.type === 'quiz' ? (
                      <Award className="h-5 w-5 text-brand-600" />
                    ) : (
                      <MessageSquare className="h-5 w-5 text-brand-600" />
                    )}
                  </div>
                  
                  <div className="ml-4 flex-1">
                    <h3 className="text-sm font-medium text-gray-900">{activity.title}</h3>
                    
                    {activity.course && (
                      <p className="text-xs text-gray-500">{activity.course}</p>
                    )}
                    
                    {activity.preview && (
                      <p className="text-sm text-gray-500 mt-1">{activity.preview}</p>
                    )}
                    
                    {activity.score !== undefined && (
                      <div className="flex items-center mt-1">
                        <CheckCircle className="h-4 w-4 text-brand-500 mr-1" />
                        <span className="text-xs text-gray-500">Score: {activity.score}%</span>
                      </div>
                    )}
                    
                    <p className="text-xs text-gray-400 mt-1">
                      {new Date(activity.date).toLocaleDateString('fr-FR', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <FileText className="h-12 w-12 mx-auto mb-3" />
              <p>Aucune activité récente à afficher.</p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default StudentDashboard;